import PropTypes from 'prop-types';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {connect} from 'react-redux';
import {overwriteBillingAddress, updateBillingAddress} from '../../actions/address-actions';
import overwriteGenericBillingAddress
from '../../actions/paymentActions/billingAddressActions/overwriteGenericBillingAddress';
import updateGenericBillingAddress
from '../../actions/paymentActions/billingAddressActions/updateGenericBillingAddress';
import FieldContainer2 from '../../form/FieldContainer2';
import {getSettings} from '../../selectors/selectors';
import V2Components from '../../v2-components';
import addressDefinitionsByCountry from './address-definitions-by-country';
import AddressContainer from './AddressContainer';

export function BillingAddress(props) {
  const [shouldValidateAfterNextRender, setShouldValidateAfterNextRender] = useState(false);
  const fieldContainerRef = useRef(null);
  
  const processErrorFieldName = (errors) => {
    const errorsWithPrefix = [];
    errors.forEach((error) => {
      errorsWithPrefix.push({
        fieldName: `billing.address.${error.fieldName}`,
        errorCode: error.errorCode,
      });
    });
    return errorsWithPrefix;
  };
  
  const validate = useCallback(() => {
    const containerValidationResult = fieldContainerRef.current.validate();
    return {
      isValid: containerValidationResult.isValid,
      errors: (containerValidationResult.isValid)
        ? undefined : processErrorFieldName(containerValidationResult.errors),
    };
  }, [fieldContainerRef]);
  
  useEffect(() => {
    if (shouldValidateAfterNextRender) {
      validate();
      setShouldValidateAfterNextRender(false);
    }
  }, [shouldValidateAfterNextRender, validate]);
  
  const validateAfterNextRender = () => {
    setShouldValidateAfterNextRender(true);
  };
  
  if (!props.showForm) {
    V2Components.clear('billingAddress');
    return null;
  }
  V2Components.register('billingAddress', {validate, validateAfterNextRender});
  
  return (
    <div data-test="BillingAddress">
      <FieldContainer2 fieldUpdateDispatcher={props.updateAddressFieldDispatcher}>
        {(fieldContainer) => {
          fieldContainerRef.current = fieldContainer;
          return (
            <AddressContainer
              addressType="billing"
              countries={props.countries}
              onCountryChange={props.updateCountry}
              fieldValues={props.useNewDispatcher ? props.billingAddressValues : props.fieldValues}
              definedFields={props.definedBillingAddress || addressDefinitionsByCountry.get(props.fieldValues.country)}
              isIncludeExtraFields
              fieldNamePrefix="paymentCard."
              fieldContainer={fieldContainer}
              resetAddress={props.resetAddress}
              restrictDeliveryCountry={props.restrictDeliveryCountry}
              hideCountryRegionDropdown={props.hideCountryRegionDropdown}
              useNewDispatcherForBillingAddress={props.useNewDispatcher}
            />
          );
        }}
      </FieldContainer2>
    </div>
    
  );
}

BillingAddress.propTypes = {
  countries: PropTypes.array.isRequired,
  fieldValues: PropTypes.object.isRequired,
  billingAddressValues: PropTypes.object,
  definedBillingAddress: PropTypes.object,
  showForm: PropTypes.bool.isRequired,
  resetAddress: PropTypes.func.isRequired,
  updateAddressFieldDispatcher: PropTypes.func.isRequired,
  updateCountry: PropTypes.func.isRequired,
  restrictDeliveryCountry: PropTypes.func.isRequired,
  paymentMethod: PropTypes.string,
  useNewDispatcher: PropTypes.bool,
  hideCountryRegionDropdown: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  countries: state.address.billingAddress.countries,
  fieldValues: state.address.billingAddress.values,
  hideCountryRegionDropdown: getSettings(state)?.hide_country_region_dropdown_billing,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  resetAddress: newValues => (ownProps.useNewDispatcher
    ? dispatch(overwriteGenericBillingAddress(ownProps.paymentMethod, newValues))
    : dispatch(updateBillingAddress(newValues))),
  updateCountry: event => (ownProps.useNewDispatcher
    ? dispatch(updateGenericBillingAddress('country', event.target.value, ownProps.paymentMethod))
    : dispatch(overwriteBillingAddress({country: event.target.value}))),
  updateAddressFieldDispatcher: (fieldName, value) => (ownProps.useNewDispatcher
    ? dispatch(updateGenericBillingAddress(fieldName, value, ownProps.paymentMethod))
    : dispatch(updateBillingAddress({[fieldName]: value}))),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(BillingAddress);
